import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, CheckCircle2, Clock, X, Plus, Edit3, ExternalLink, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * OrganizerCard — Account-page widget that shows the user's organizer status and lets
 * them submit a claim. Approved organizers get a "Create fundraiser" button + their list.
 */
const OrganizerCard = () => {
    const { user } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        role_claim: "parent",
        school: "",
        grade: "",
        note_to_admin: "",
    });
    const [mine, setMine] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [creating, setCreating] = useState(false);
    const [fundraiserForm, setFundraiserForm] = useState({
        campaign_id: "",
        title: "",
        story: "",
        goal_amount: "",
        photo_url: "",
    });

    const status = user?.organizer_status;
    const isApproved = status === "approved";

    useEffect(() => {
        if (!isApproved) return;
        api.get("/my/fundraisers").then((r) => setMine(r.data || [])).catch(() => {});
        api.get("/campaigns").then((r) => setCampaigns((r.data || []).filter((c) => c.is_active !== false))).catch(() => {});
    }, [isApproved]);

    const submitRequest = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/my/organizer-request", form);
            toast.success(
                data.fast_flag
                    ? "Request submitted — teachers are fast-tracked, expect a quick reply."
                    : "Request submitted — we'll review and get back to you soon."
            );
            setOpen(false);
            // Reload user via window reload so the AuthContext re-fetches
            setTimeout(() => window.location.reload(), 800);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    const createFundraiser = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data } = await api.post("/my/fundraisers", {
                ...fundraiserForm,
                goal_amount: parseFloat(fundraiserForm.goal_amount),
            });
            toast.success("Fundraiser created!");
            setFundraiserForm({ campaign_id: "", title: "", story: "", goal_amount: "", photo_url: "" });
            const r = await api.get("/my/fundraisers");
            setMine(r.data || []);
            window.open(`/fundraise/${data.slug}`, "_blank");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="mt-12" data-testid="account-organizer-section">
            <div className="mb-6 flex items-center gap-3">
                <Megaphone className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Peer-to-peer fundraising</h2>
            </div>

            {/* Status pill */}
            {!status && (
                <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="account-organizer-not-requested">
                    <p className="text-sm text-[#4A5568] mb-4 max-w-2xl">
                        Want to raise money for a foundation campaign on behalf of your class, team, or club?
                        Become an approved organizer and you can create your own personal fundraising page tied
                        to any active Reddog campaign.
                    </p>
                    {!open ? (
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="account-organizer-request-btn"
                            className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium"
                        >
                            <Plus className="w-4 h-4" />
                            Request to fundraise
                        </button>
                    ) : (
                        <form onSubmit={submitRequest} className="bg-[#FDFBF7] rounded-xl p-4 space-y-3" data-testid="account-organizer-form">
                            <div>
                                <label htmlFor="role-claim-select" className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1.5">I am a</label>
                                <select
                                    id="role-claim-select"
                                    value={form.role_claim}
                                    onChange={(e) => setForm({ ...form, role_claim: e.target.value })}
                                    data-testid="account-organizer-role-select"
                                    className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                                >
                                    <option value="teacher">Teacher (fast-tracked review)</option>
                                    <option value="parent">Parent</option>
                                    <option value="student">Student</option>
                                    <option value="community">Community member</option>
                                </select>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder="School (optional)"
                                    maxLength={120}
                                    value={form.school}
                                    onChange={(e) => setForm({ ...form, school: e.target.value })}
                                    data-testid="account-organizer-school-input"
                                    aria-label="School"
                                    className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                                />
                                <input
                                    type="text"
                                    placeholder="Grade / class (optional)"
                                    maxLength={40}
                                    value={form.grade}
                                    onChange={(e) => setForm({ ...form, grade: e.target.value })}
                                    data-testid="account-organizer-grade-input"
                                    aria-label="Grade"
                                    className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                                />
                            </div>
                            <textarea
                                placeholder="Anything you want the admin to know (what you'd like to fundraise for, etc.)"
                                rows={2}
                                maxLength={500}
                                value={form.note_to_admin}
                                onChange={(e) => setForm({ ...form, note_to_admin: e.target.value })}
                                data-testid="account-organizer-note-input"
                                aria-label="Note to admin"
                                className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm resize-none"
                            />
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="account-organizer-submit-btn"
                                    className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    Submit request
                                </button>
                                <button type="button" onClick={() => setOpen(false)} className="text-sm text-[#4A5568] hover:text-[#1C252E] px-3">Cancel</button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {status === "pending" && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-3" data-testid="account-organizer-pending">
                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div>
                        <p className="font-medium text-[#1C252E]">Request pending</p>
                        <p className="text-sm text-[#4A5568]">A board member is reviewing your request. You'll get an email as soon as it's approved.</p>
                    </div>
                </div>
            )}

            {status === "rejected" && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-3" data-testid="account-organizer-rejected">
                    <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <div>
                        <p className="font-medium text-[#1C252E]">Request not approved</p>
                        {user?.organizer_admin_note && <p className="text-sm text-[#4A5568] mt-1">Admin note: <em>{user.organizer_admin_note}</em></p>}
                        <p className="text-sm text-[#4A5568] mt-2">If you have additional context, please reply to the rejection email.</p>
                    </div>
                </div>
            )}

            {isApproved && (
                <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="account-organizer-approved">
                    <div className="flex items-center gap-2 mb-4">
                        <CheckCircle2 className="w-5 h-5 text-green-600" strokeWidth={2} />
                        <p className="text-sm font-medium text-[#1C252E]">You're an approved organizer.</p>
                    </div>

                    {/* Existing fundraisers */}
                    {mine.length > 0 && (
                        <div className="mb-6 space-y-2" data-testid="account-organizer-fundraisers">
                            {mine.map((f) => {
                                const pct = f.goal_amount > 0 ? Math.min(100, Math.round((f.raised / f.goal_amount) * 100)) : 0;
                                return (
                                    <div key={f.id} className="bg-[#FDFBF7] rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`account-fundraiser-${f.id}`}>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-[#1C252E] truncate">{f.title}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <div className="flex-1 h-1.5 bg-black/5 rounded-full overflow-hidden max-w-xs">
                                                    <div className="h-full bg-[#E53935]" style={{ width: `${pct}%` }} />
                                                </div>
                                                <span className="text-xs text-[#4A5568] whitespace-nowrap">
                                                    ${(f.raised || 0).toFixed(0)} / ${f.goal_amount.toFixed(0)} · {f.donor_count || 0} donors
                                                </span>
                                            </div>
                                        </div>
                                        <Link
                                            to={`/fundraise/${f.slug}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`account-fundraiser-link-${f.id}`}
                                            className="text-xs inline-flex items-center gap-1 text-[#E53935] hover:underline"
                                        >
                                            View <ExternalLink className="w-3 h-3" />
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Create form */}
                    <form onSubmit={createFundraiser} className="bg-[#FDFBF7] rounded-xl p-4 space-y-3" data-testid="account-fundraiser-create-form">
                        <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Create a new fundraiser</p>
                        <select
                            required
                            value={fundraiserForm.campaign_id}
                            onChange={(e) => setFundraiserForm({ ...fundraiserForm, campaign_id: e.target.value })}
                            data-testid="account-fundraiser-campaign-select"
                            aria-label="Foundation campaign"
                            className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                        >
                            <option value="">— Pick a foundation campaign —</option>
                            {campaigns.map((c) => (
                                <option key={c.id} value={c.id}>{c.title}</option>
                            ))}
                        </select>
                        <input
                            required
                            type="text"
                            placeholder="Your fundraiser title (e.g. 'Help Mrs. Smith's class get to the Liberty Science Center')"
                            maxLength={120}
                            value={fundraiserForm.title}
                            onChange={(e) => setFundraiserForm({ ...fundraiserForm, title: e.target.value })}
                            data-testid="account-fundraiser-title-input"
                            aria-label="Fundraiser title"
                            className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                        />
                        <textarea
                            required
                            placeholder="Tell your story — why does this matter to you?"
                            rows={3}
                            maxLength={3000}
                            value={fundraiserForm.story}
                            onChange={(e) => setFundraiserForm({ ...fundraiserForm, story: e.target.value })}
                            data-testid="account-fundraiser-story-input"
                            aria-label="Story"
                            className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white text-sm resize-none"
                        />
                        <div className="grid sm:grid-cols-2 gap-3">
                            <input
                                required
                                type="number"
                                min="1"
                                step="0.01"
                                placeholder="Goal amount ($)"
                                value={fundraiserForm.goal_amount}
                                onChange={(e) => setFundraiserForm({ ...fundraiserForm, goal_amount: e.target.value })}
                                data-testid="account-fundraiser-goal-input"
                                aria-label="Goal amount"
                                className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Photo URL (optional)"
                                maxLength={500}
                                value={fundraiserForm.photo_url}
                                onChange={(e) => setFundraiserForm({ ...fundraiserForm, photo_url: e.target.value })}
                                data-testid="account-fundraiser-photo-input"
                                aria-label="Photo URL"
                                className="rounded-lg border border-black/10 px-3 py-2 bg-white text-sm"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={creating}
                            data-testid="account-fundraiser-submit-btn"
                            className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium"
                        >
                            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
                            Create fundraiser
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default OrganizerCard;
